import { useMemo } from 'react'

import { FxAlert } from '@/components/fx'

import { FieldsPanel } from './template/FieldsPanel'
import { LayoutsPanel } from './template/LayoutsPanel'
import { fieldRows, layoutEntries, variantTargets } from './template/templateTabModel'
import { useTemplateEdits } from './template/useTemplateEdits'
import { useCockpitContext } from './useCockpit'

/**
 * The cockpit's "Vorlage" tab: what the form looks like, in every Fassung it
 * exists in, and which fields those Fassungen share.
 *
 * Two panels, in the order the work happens: the layouts first, because a
 * variant is what someone came here to add, and the field matrix under them,
 * because it is only readable once you know which layouts its columns are.
 *
 * The box editor is a screen of its own (`/templates/:id/bearbeiten/:page`,
 * a later wave). This tab links into it and never draws a box: everything it
 * writes goes through the page and field endpoints, which are additive and
 * refuse rather than overwrite. The one exception is the field reorder, which
 * has to use the whole-document PATCH — see `useTemplateEdits.moveField` for
 * what it does about that.
 */
export default function TemplateTab() {
  const cockpit = useCockpitContext()
  const { template, counts, canWrite, templateId } = cockpit
  const edits = useTemplateEdits(cockpit)

  const entries = useMemo(() => layoutEntries(template, counts), [template, counts])
  const targets = useMemo(() => variantTargets(template), [template])
  const rows = useMemo(() => fieldRows(template), [template])

  // The layout renders the tab only once the template has loaded; this is the
  // impossible case, not a loading state.
  if (!template) return null

  return (
    <div className="flex flex-col gap-6">
      {entries.length === 0 && (
        <FxAlert tone="warning" title="Dieses Musterformular hat keine Seiten">
          Vermutlich ist beim Hochladen etwas schiefgegangen. Laden Sie das leere Formular noch
          einmal hoch oder legen Sie es neu an.
        </FxAlert>
      )}

      <LayoutsPanel
        templateId={templateId}
        entries={entries}
        targets={targets}
        canWrite={canWrite}
        edits={edits}
        onRefresh={() => void cockpit.invalidate.template()}
      />

      <FieldsPanel
        templateId={templateId}
        rows={rows}
        layouts={entries}
        fields={template.fields}
        canWrite={canWrite}
        edits={edits}
      />
    </div>
  )
}
